import { useContext, useMemo } from "react";
import { Button, Icon, IconButton, Typography } from "@mui/material";
import { toastr } from "react-redux-toastr";

import { subscriptionService } from "@/features/subscriptions/services/subscriptionService";
import { getFormatDate } from "@/helpers/utils";
import { GlobalContext, LoaderContext } from "@/helpers/contexts/contexts";
import { subscriptionHelper } from "@/features/subscriptions/helpers/subscriptionHelper";
import { HTTP_STATUS } from "@/config/constants";

type SubscriptionLocation = {
  current_package_payment_type?: string;
  fk_location_id?: number | string;
  fk_participant_id?: number | string;
  fk_user_id?: number | string;
  is_current_subscription_active?: boolean;
  location_name?: string;
  subscription_end_date?: string;
  system_generated_location_id?: string;
  taken_upcoming_subscription?: boolean;
  upcoming_end_date?: string;
  upcoming_package_payment_type?: string;
};

type OptOutConfirmationProps = {
  close: () => void;
  fetchLocations: (_force?: boolean) => Promise<void> | void;
  location: SubscriptionLocation;
  onOptOutSuccess?: () => void;
};

type OptOutContext = {
  selectedBrand?: {
    id?: number | string;
    is_white_label?: boolean;
  } | null;
};

type Loader = (_isLoading: boolean) => void;

type ServiceResponse = {
  status?: number;
};

type ServiceError = {
  response?: {
    status?: number;
  };
};

export default function OptOutConfirmation({
  location,
  close,
  fetchLocations,
  onOptOutSuccess,
}: OptOutConfirmationProps) {
  const { selectedBrand } = useContext(GlobalContext) as OptOutContext;
  const loader = useContext(LoaderContext) as Loader;

  const isNewSubscription = subscriptionHelper.checkIsNewSubscription(location)
    ? true
    : false;

  const handleOnOptOut = async () => {
    loader(true);
    try {
      const reqData = {
        system_generated_location_id: location?.system_generated_location_id,
        fk_location_id: location?.fk_location_id,
        last_subscription_date: location?.taken_upcoming_subscription
          ? location?.upcoming_end_date
          : location?.subscription_end_date,
        taken_upcoming_subscription: location?.taken_upcoming_subscription,
        fk_brand_id: selectedBrand?.id,
        fk_user_id: location?.fk_user_id,
        fk_participant_id: location?.fk_participant_id,
        location_name: location?.location_name,
        is_canceled: isNewSubscription,
        payment_type: location.is_current_subscription_active
          ? location?.upcoming_package_payment_type
          : location?.current_package_payment_type,
        is_white_label: selectedBrand?.is_white_label,
      };

      const res = (await subscriptionService.optOutAutoRenewal(
        reqData
      )) as ServiceResponse;
      if (res.status === HTTP_STATUS.OK) {
        onOptOutSuccess?.();
        await fetchLocations(true);
        loader(false);
        toastr.success(
          "Success",
          `You have successfully ${
            isNewSubscription
              ? "canceled the upcoming subscription"
              : "opt-out Auto-Renewal"
          }  for the location ${location?.location_name}`
        );
        // close();
      }
    } catch (err) {
      const error = err as ServiceError;
      if (error.response?.status === 406) {
        await fetchLocations(true);
      }
      loader(false);
    } finally {
      // await fetchLocations(true);
      // loader(false);
      setTimeout(() => {
        close();
      }, 1000);
    }
  };

  const endDate = useMemo(() => {
    const {
      subscription_end_date,
      taken_upcoming_subscription,
      upcoming_end_date,
    } = location;
    if (taken_upcoming_subscription) {
      return upcoming_end_date;
    }
    return subscription_end_date;
  }, [location]);

  return (
    <div className="opt-out-confirmation text-center">
      <div className="d-flex justify-content-end">
        <IconButton
          aria-label="close"
          onClick={close}
          color="inherit"
          className="p-0"
        >
          <Icon fontSize="small">highlight_off</Icon>
        </IconButton>
      </div>
      <div className="container">
        <div>
          <img
            src="/img/icons/info.svg"
            alt="alert-icon"
            className="img-fluid"
          />
        </div>
        <Typography
          component="h3"
          variant="h5"
          className="main-heading text-center my-4"
          gutterBottom
        >
          Are you sure you want to{" "}
          {isNewSubscription
            ? " cancel your subscription"
            : "turn off your automatic renewal"}
          ?
        </Typography>
        {!isNewSubscription && (
          <Typography variant="h6" component="p">
            You will still be able to renew your subscription at any point in
            this page. Your Subscription will conclude on <br />
            <Typography variant="h6" component="span" className="fw-bb">
              {getFormatDate(endDate, "MM/DD/YYYY")}
            </Typography>
            .
          </Typography>
        )}

        <div className="mt-5 mb-3 row justify-content-center align-items-center">
          <div className="col-4">
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              onClick={close}
            >
              No
            </Button>
          </div>
          <div className="col-5">
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleOnOptOut}
            >
              Yes
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
